import TimeSignature from 'Models/TimeSignature';
import { GameObjects, Scene } from 'phaser';
import { colors } from 'Phaser/config';
import IPlayable from './Playable';
import { ProgressState } from './Progress';
import { Fingering, PrerenderedPhraseData } from 'Types';
import { BaseNote, getMidiNumberFromNoteTuple, noteFromOsmdPitch } from 'Phaser/Util/OSMDHelper';
import { Dissolve } from 'Phaser/Rendering/Transitions';

export type StaffTransition = 'Draw' | 'Fade' | 'None';

interface Config {
  globalScaling: number;
  pitchLineGap: number;
  phraseWidth: number;
  pitchLineWidth: number;
  measureLineWidth: number;
  noteScaling: number;
  staffSpacingVertical: number;
  notePaddingFactor: number;
  // The gap between the first note in a measure and the measure line.
  // In case of a measure which contains the clef symbol, the total
  // gap between the first note and the measure line for that measure
  // will be firstNoteGap + any other symbols that appear before it
  // such as time signatures, clefs, etc.
  firstNoteGap: number;
  clefSymbolMargin: number;
  centreAlign: boolean;
}

export function DefaultConfig(config: Partial<Config>): Config {
  const dpr = 1;
  const def = {
    globalScaling: (config.globalScaling ?? 1) * dpr,
    noteScaling: (config.noteScaling ?? 1) * dpr,
    phraseWidth: (config.phraseWidth ?? 1440) * dpr,
    pitchLineWidth: (config.pitchLineWidth ?? 4) * dpr,
    pitchLineGap: (config.pitchLineGap ?? 40) * dpr,
    measureLineWidth: (config.measureLineWidth ?? 6) * dpr,
    staffSpacingVertical: (config.staffSpacingVertical ?? 200) * dpr,
    firstNoteGap: (config.firstNoteGap ?? 40) * dpr,
    clefSymbolMargin: (config.clefSymbolMargin ?? 20) * dpr,
    centreAlign: config.centreAlign ?? false,
    notePaddingFactor: config.notePaddingFactor ?? 1,
  };
  return def;
}


export interface VoiceData {
  fingering: Fingering | undefined;
  x: number;
  measureIdx: number;
  timestamp: number;
  voice: 'rest' | { note: [BaseNote, number] };
  id: string;
  length: number;
}

export type ClefType = 'bass' | 'treble';

class RestGraphic extends Phaser.GameObjects.Container implements IPlayable {
  measureIdx: number;
  isRest = true;
  idx: number;
  resetTimer?: Phaser.Time.TimerEvent;
  isFocused: boolean = false;
  timingState?: ProgressState;
  id: string;
  tweenHandle?: Phaser.Tweens.Tween;
  restGraphic?: Phaser.GameObjects.Image;
  restRectangle?: Phaser.GameObjects.Rectangle;
  dot?: Phaser.GameObjects.Arc;

  constructor(
    scene: Phaser.Scene,
    measureIdx: number,
    id: string,
    x: number,
    line: number,
    length: number,
    { pitchLineGap, pitchLineWidth }: Config,
    parent: Clef,
    idx: number,
    timeSigNum: number
  ) {
    super(scene);
    scene.add.existing(this);
    this.measureIdx = measureIdx;
    let lengthToRestTexture = '';
    let dotted = false;
    if (length === timeSigNum * 0.25) {
      lengthToRestTexture = 'whole_rest';
    } else {
      switch (length) {
        case 0.25:
          lengthToRestTexture = 'quarter_rest';
          break;
        case 0.3125:
          lengthToRestTexture = 'quarter_rest';
          dotted = true;
          break;
        case 0.5:
          lengthToRestTexture = 'half_rest';
          break;
        case 0.75:
          lengthToRestTexture = 'half_rest';
          dotted = true;
          break;
        case 1:
          lengthToRestTexture = 'whole_rest';
          break;
        case 1.5:
          lengthToRestTexture = 'whole_rest';
          dotted = true;
          break;
        default:
          console.error(
            'Only half, quarter and whole rests supported for now!'
          );
      }
    }
    let l = line / 2 - (lengthToRestTexture === 'whole_rest' ? 1 : 0);
    let y = l * pitchLineGap;
    let desiredHeight = 0;
    switch (length) {
      case 0.25:
        desiredHeight = 3 * pitchLineGap;
        break;
      default:
        desiredHeight = 0.6 * pitchLineGap;
        break;
    }
    //super(scene, x, y, lengthToRestTexture);
    let graphic: Phaser.GameObjects.Image | Phaser.GameObjects.Rectangle;
    if (lengthToRestTexture === 'quarter_rest') {
      this.restGraphic = scene.add.image(x, y, lengthToRestTexture);
      graphic = this.restGraphic;
    } else {
      //const halfRestOffset = (length === 0.5 && length !== timeSigNum * 0.25) ? 2 : 0;
      this.restRectangle = scene.add.rectangle(x, y, 50, 20, 0, 1);
      graphic = this.restRectangle;
    }
    this.add(graphic);
    let scale = desiredHeight / graphic.displayHeight;
    graphic.setScale(scale);
    if (dotted) {
      const dotX = graphic.x + graphic.displayWidth * 1.2;
      let yOffset = 0;
      const dotR = graphic.displayWidth / 6;
      if (line % 2 === 0) {
        yOffset = graphic.displayHeight / 3 + dotR / 2;
      }
      const dotY = graphic.y - yOffset;
      this.dot = scene.add.circle(dotX, dotY, dotR, 0x0, 1);
      this.add(this.dot);
    }
    if (length === 0.5 && length !== timeSigNum * 0.25) {
      this.restRectangle!.setOrigin(0.5, 1);
      this.restRectangle!.y += pitchLineWidth;
    } else if (length === 1 || length === timeSigNum * 0.25) {
      this.restRectangle!.setOrigin(0.5, 0);
    }
    parent.add(this);
    this.id = id;
    this.idx = idx;
    this.restRectangle?.setAlpha(0);
    this.restGraphic?.setAlpha(0);
    this.dot?.setAlpha(0);
  }

  getY() {
    return this.y;
  }

  update(_: number) { }

  destroy() {
    //this.resetTimer?.remove();
    this.tweenHandle?.remove();
    this.tweenHandle = undefined;
    this.isFocused = false;
    super.destroy();
  }

  setFocus(focus: boolean) {
    this.isFocused = focus;
  }

  transition(style: StaffTransition, idx: number) {
    switch (style) {
      case 'Draw':
        const props = {
          rectScale: this.restRectangle?.scale,
          graphicScale: this.restGraphic?.scale,
          dotScale: this.dot?.scale
        };
        this.restGraphic?.setScale(0);
        this.restRectangle?.setScale(0);
        this.dot?.setScale(0);
        const onComplete = () => {
          if (this.dot) this.addTween({
            targets: [this.dot],
            scale: props.dotScale,
            alpha: 1,
            duration: 200
          })
        };
        if (this.restRectangle) this.addTween({
          targets: [this.restRectangle],
          alpha: 1,
          scale: props.rectScale,
          duration: 300,
          onComplete
        });
        if (this.restGraphic) this.addTween({
          targets: [this.restGraphic],
          alpha: 1,
          scale: props.graphicScale,
          duration: 300,
          onComplete
        });
        break;
      case 'None':
        this.restRectangle?.setAlpha(1);
        this.restGraphic?.setAlpha(1);
        this.dot?.setAlpha(1);
        break;
      case 'Fade':
        this.addTween({
          targets: [this.restRectangle, this.restGraphic, this.dot],
          alpha: 1,
          duration: 500,
        });
        break;
    }
  }

  addTween(
    tween: Phaser.Tweens.Tween | Phaser.Types.Tweens.TweenBuilderConfig
  ) {
    this.tweenHandle = this.scene.tweens.add(tween);
  }

  setTimingState(state: ProgressState) {
    this.timingState = state;
    switch (state) {
      case ProgressState.LateStart:
      case ProgressState.MistimedRelease:
        this.setTintFill(colors.noteFillOffTime);
        break;
      case ProgressState.Ended:
        this.setTintFill(colors.noteFillOnTime);
        break;
      case ProgressState.Missed:
        this.setTintFill(colors.noteFillMiss);
        break;
      default:
    }
  }

  setTintFill(color: number | string) {
    let col = Phaser.Display.Color.ValueToColor(color).color;
    this.restGraphic?.setTintFill(col);
    this.restRectangle?.setFillStyle(col);
    this.dot?.setFillStyle(col);
  }

  getState() {
    return this.timingState;
  }

  shake() { }

  reset(force: boolean) {
    this.timingState = undefined;
    const delay = force ? 0 : 2000 + this.idx * 100;
    this.resetTimer = this.scene.time.delayedCall(
      delay,
      () => {
        this.restGraphic?.setTintFill(0);
        this.dot?.setFillStyle(0);
      },
      [],
      this
    );
  }

  showFingering(parent: Phaser.GameObjects.Container, max: number, min: number) { }

  getPosition() {
    return { x: this.restGraphic?.x ?? this.restRectangle!.x, y: this.restGraphic?.y ?? this.restRectangle!.y };
  }

  getHaloOffset() {
    return 0;
  }

  setColor(_: number | string) {
    //this.graphic.setTintFill(color);
  }

  pulse() { }
}

interface NoteTransitionState {
  stemHeight?: number;
  headScale: number;
  dotScale?: number;
  ledgerLineWidth?: number;
};

export class NoteGraphic extends Phaser.GameObjects.Container implements IPlayable {

  isRest = false;
  idx: number;
  resetTimer?: Phaser.Time.TimerEvent;
  isFocused: boolean = false;
  timestamp: number;
  measureIdx: number;
  timingState?: ProgressState;
  noteHead!: Phaser.GameObjects.Image;
  stem?: Phaser.GameObjects.Rectangle;
  ledgerLines: Phaser.GameObjects.Rectangle[] = [];
  noteLength: number;
  id: string;
  tweenHandles: Phaser.Tweens.Tween[] = [];
  noteDot?: Phaser.GameObjects.Arc;
  pulseObject?: Phaser.GameObjects.Arc;
  pulseTween?: Phaser.Tweens.Tween;
  originalPosition: number;
  fingering?: Fingering;
  fingeringGraphic?: Phaser.GameObjects.Text;
  stemDirection: "up" | "down";

  transitionState?: NoteTransitionState;
  midiNote: number;

  constructor(
    scene: Scene,
    measureIdx: number,
    id: string,
    parent: Clef,
    x: number,
    line: number,
    length: number,
    { pitchLineGap, pitchLineWidth }: Config,
    timestamp: number,
    idx: number,
    midiNote: number,
    fingering?: Fingering,
  ) {
    super(scene);
    this.midiNote = midiNote;
    this.measureIdx = measureIdx;
    this.idx = idx;
    this.fingering = fingering;
    this.timestamp = timestamp;
    this.noteLength = length;
    this.id = id;
    scene.add.existing(this);
    const yPos = (line / 2) * pitchLineGap;
    let dotted = false;
    switch (length) {
      case 0.25:
        this.noteHead = scene.add.image(x, yPos, 'quarter_note');
        break;
      case 0.3125:
        this.noteHead = scene.add.image(x, yPos, 'quarter_note');
        dotted = true;
        break;
      case 0.5:
        this.noteHead = scene.add.image(x, yPos, 'half_note');
        break;
      case 0.75:
        this.noteHead = scene.add.image(x, yPos, 'half_note');
        dotted = true;
        break;
      case 1:
        this.noteHead = scene.add.image(x, yPos, 'whole_note');
        break;
      case 1.5:
        this.noteHead = scene.add.image(x, yPos, 'whole_note');
        dotted = true;
        break;
      default:
        console.error('Only half, quarter and whole notes supported for now!');
    }
    this.noteHead.tintFill = true;
    this.noteHead.tint = 0;
    this.noteHead.setScale(pitchLineGap / this.noteHead.displayHeight);
    this.add(this.noteHead);
    if (dotted) {
      const dotX = this.noteHead.x + this.noteHead.displayWidth * 1.2;
      let yOffset = 0;
      const dotR = this.noteHead.displayWidth / 6;
      if (line % 2 === 0) {
        yOffset = this.noteHead.displayHeight / 3 + dotR / 2;
      }
      const dotY = this.noteHead.y - yOffset;
      this.noteDot = scene.add.circle(dotX, dotY, dotR, 0x0, 1);
      this.add(this.noteDot);
    }
    const ledgerLineWidth = this.noteHead.displayWidth * 1.5;
    // The stem width should be relative to the width of the note head itself,
    // otherwise it doesn't scale properly along with everything else.
    const stemWidth = this.noteHead.displayWidth * 0.1;
    const stemHeight = pitchLineGap * 3.5;
    let stemYOffset = stemHeight / 2;
    let stemXOffset = -this.noteHead.displayWidth / 2 + stemWidth / 2;
    if (line >= 5) {
      this.stemDirection = "up";
      stemYOffset *= -1;
      stemXOffset *= -1;
    } else {
      this.stemDirection = "down";
    }
    this.stem = [0.25, 0.375, 0.5, 0.75].contains(length)
      ? scene.add.rectangle(
        x + stemXOffset,
        yPos + stemYOffset,
        stemWidth,
        stemHeight,
        0,
        1
      )
      : undefined;
    this.stem && this.add(this.stem);
    if (line < 0) {
      const numLedgerLines = Math.floor((0 - line) / 2);
      for (let i = 0; i < numLedgerLines; i++) {
        this.ledgerLines.push(
          scene.add.rectangle(
            x,
            (-i - 1) * pitchLineGap,
            ledgerLineWidth,
            pitchLineWidth,
            0x0,
            1
          )
        );
      }
    } else {
      const numLedgerLines = Math.floor((line - 8) / 2);
      for (let i = 0; i < numLedgerLines; i++) {
        this.ledgerLines.push(
          scene.add.rectangle(
            x,
            (i + 5) * pitchLineGap,
            ledgerLineWidth,
            pitchLineWidth,
            0x0,
            1
          )
        );
      }
    }
    this.ledgerLines.forEach((line) => {
      line.setAlpha(0);
      this.add(line);
      this.sendToBack(line);
    });
    parent.add(this);
    this.originalPosition = this.x;
    this.noteHead.alpha = 0;
    this.stem?.setAlpha(0);
    this.noteDot?.setAlpha(0);
  }

  getY() {
    return this.noteHead.y;
  }

  transition(style: StaffTransition) {
    switch (style) {
      case 'Draw':
        const transitionState = {
          stemHeight: this.stem?.height,
          headScale: this.noteHead.scale,
          dotScale: this.noteDot?.scale,
          ledgerLineWidth: this.ledgerLines[0]?.width,
          stemY: this.stem?.y
        };
        this.ledgerLines.forEach((line) => (line.width = 0));
        this.noteHead.setScale(0);
        this.noteDot?.setScale(0);
        if (this.stem) {
          this.stem.setY(this.stem.y + this.stem.displayHeight * (this.stemDirection === "up" ? 1 : 0));
          this.stem.height = 0;
        }
        this.addTween({
          targets: [this.noteHead],
          alpha: 1,
          scale: transitionState.headScale,
          duration: 500,
          ease: "Power2",
          onComplete: () => {
            this.addTween({
              targets: [...this.ledgerLines],
              alpha: 1,
              width: transitionState.ledgerLineWidth,
              duration: 500
            });
            if (this.stem) this.addTween({
              targets: [this.stem],
              alpha: 1,
              y: transitionState.stemY,
              height: transitionState.stemHeight,
              duration: 500,
              ease: "Power2"
            });
            if (this.noteDot) this.addTween({
              targets: [this.noteDot],
              scale: transitionState.dotScale,
              alpha: 1,
              duration: 500,
              ease: "Power2"
            });
          }
        });
        break;
      case 'None':
        this.ledgerLines.forEach((line) => (line.alpha = 1));
        this.noteHead.setAlpha(1);
        this.noteDot?.setAlpha(1);
        this.stem?.setAlpha(1);
        break;
      case 'Fade':
        this.addTween({
          targets: [
            ...[this.noteHead],
            ...[this.noteDot],
            ...[this.stem],
            ...this.ledgerLines,
          ],
          alpha: 1,
          duration: 500,
          ease: "Power2"
        });
        break;
    }
  }

  addTween(
    tween: Phaser.Tweens.Tween | Phaser.Types.Tweens.TweenBuilderConfig
  ) {
    this.tweenHandles.push(this.scene.tweens.add(tween));
  }

  update(_: number) { }

  destroy() {
    this.tweenHandles.forEach((tween) => tween.remove());
    this.resetTimer?.remove();
    this.isFocused = false;
    this.fingeringGraphic?.destroy();
    this.stem?.destroy();
    this.ledgerLines.forEach((l) => l.destroy());
    this.ledgerLines.clear();
    this.noteHead.destroy();
    this.pulseTween?.stop();
    this.pulseObject?.destroy();
    this.noteDot?.destroy();
    super.destroy();
  }

  setFocus(focus: boolean) {
    this.isFocused = focus;
    this.tweenHandles.forEach((tween) => tween.remove());
  }

  showFingering(
    parent: Phaser.GameObjects.Container,
    max: number,
    min: number,
    staffPosition: 'top' | 'bottom',
    scale: number
  ) {
    if (this.fingering) {
      const y = staffPosition === 'top' ? max : min;
      const x = this.getBounds().x + this.getBounds().width / 2;
      console.debug('finger', staffPosition, max, min);
      this.fingeringGraphic = this.scene.add.text(x, y, this.fingering.value, {
        color: '#000',
        fontSize: 32,
        fontFamily: 'Times New Roman',
      });
      if (staffPosition === "top") this.fingeringGraphic.setOrigin(0.5, 1);
      else this.fingeringGraphic.setOrigin(0.5, 0);
      parent.add(this.fingeringGraphic);
    }
  }

  setTintFill(color: number) {
    this.stem && (this.stem.fillColor = color);
    this.noteHead.setTintFill(color);
    this.ledgerLines.forEach((line) => line.setFillStyle(color));
    this.noteDot && (this.noteDot.fillColor = color);
  }

  setTimingState(state: ProgressState) {
    this.timingState = state;
    switch (state) {
      case ProgressState.LateStart:
      case ProgressState.MistimedRelease:
        this.setTintFill(colors.noteFillOffTime);
        break;
      case ProgressState.Ended:
        this.setTintFill(colors.noteFillOnTime);
        break;
      case ProgressState.Missed:
        this.setTintFill(colors.noteFillMiss);
        break;
      default:
    }
  }

  getState() {
    return this.timingState;
  }

  shake() {
    this.tweenHandles.push(
      this.scene.tweens.add({
        targets: { value: 0 },
        value: Math.PI * 2,
        duration: 250,
        repeat: 1,
        onUpdate: (_tween, _target, _key, current) => {
          console.log(current);
          this.setX(this.originalPosition + Math.sin(current) * 10 * 1);
        },
      })
    );
  }

  reset(force: boolean) {
    this.timingState = undefined;
    const cb = () => {
      this.setTintFill(0);
      this.noteHead.tintFill = true;
      this.pulseObject?.destroy();
      this.pulseObject = undefined;
      this.pulseTween?.remove();
      this.tweenHandles.forEach((tween) => tween.remove());
      this.setX(this.originalPosition);
    };
    /*this.addTween({
      targets: this.noteHead,
      tint: 0,
      duration: force ? 0 : 250,
      delay: force ? 0 : this.idx * 50 + 2000,
    });*/
    /*this.addTween({
      targets: [this.stem, ...this.ledgerLines, this.noteDot],
      fillColor: 0,
      duration: force ? 0 : 250,
      delay: force ? 0 : this.idx * 50 + 2000,
    });*/
    //};
    if (force) {
      this.resetTimer?.remove();
      cb.call(this);
    }
    else {
      this.resetTimer = this.scene.time.delayedCall(
        2000, //+ this.idx * 50,
        cb,
        [],
        this
      );
    }
  }

  getPosition() {
    return { x: this.noteHead.x, y: this.noteHead.y };
  }

  getHaloOffset() {
    if (this.noteLength !== 1) {
      return this.height / 3.75;
    } else {
      return 0;
    }
  }

  pulse() {
    if (!this.pulseObject) {
      this.pulseObject = this.scene.add.circle(
        this.noteHead.x,
        this.noteHead.y,
        0,
        colors.green,
        1
      );
      this.add(this.pulseObject);
    }
    this.pulseTween = this.scene.tweens.add({
      targets: this.pulseObject,
      radius: 40,
      alpha: 0,
      duration: 750,
      onComplete: () => {
        this.pulseObject?.setAlpha(0);
      },
      ease: 'Power2',
      callbackScope: this,
    });
  }

  setColor(color: string | number) {
    this.setTintFill(Phaser.Display.Color.ValueToColor(color).color);
  }
}

export interface ClefData {
  notes: VoiceData[];
  clef: ClefType;
  octaveOffset: number;
}

type VoiceGraphic = NoteGraphic | RestGraphic;

class Clef extends GameObjects.Container {
  pitchLines: GameObjects.Rectangle[] = [];
  barWidths: number[] = [];
  notes: Map<string, VoiceGraphic> = new Map();
  noteArray: VoiceGraphic[] = [];
  _data: ClefData;
  measureData: MeasureProps[];
  config: Config;
  clefSymbol?: Phaser.GameObjects.Image;
  timeSignatureGraphics: Phaser.GameObjects.Image[] = [];
  tweenHandles: Phaser.Tweens.Tween[] = [];
  timeSig?: TimeSignature;

  constructor(
    scene: Scene,
    config: Config,
    data: ClefData,
    measureData: MeasureProps[],
    timeSig?: TimeSignature,
    clefSymbol?: Phaser.GameObjects.Image,
    timeSignature?: Phaser.GameObjects.Image[]
  ) {
    super(scene);
    this.timeSig = timeSig;
    this.measureData = measureData;
    this.config = config;
    this._data = data;
    scene.add.existing(this);
    this.createLines();
    this.setPosition(
      0,
      data.clef === 'bass'
        ? config.pitchLineGap * 4 + config.staffSpacingVertical
        : 0
    );
    this.reset(data, measureData, clefSymbol, timeSignature);
  }

  transition(style: StaffTransition) {
    this.addTween({
      targets: [this.clefSymbol, ...this.timeSignatureGraphics],
      alpha: 1,
      duration: style === 'None' ? 0 : 500,
    });
    switch (style) {
      case 'Draw':
        const width = this.pitchLines[0].width;
        this.pitchLines.forEach((line, idx) => {
          line.width = 0;
          this.addTween({
            targets: line,
            delay: 50 * idx,
            alpha: 1,
            width,
            duration: 1000,
            ease: "Power2"
          });
        });
        this.addTween({
          targets: [this.clefSymbol],
          alpha: 1,
          duration: 500,
          delay: 500,
          ease: "Power2"
        });
        this.noteArray.forEach((note, idx) => note.transition(style, idx));
        break;
      case 'Fade':
        this.addTween({
          targets: this.pitchLines,
          alpha: 1,
          duration: 500,
        });
        this.noteArray.forEach((note, idx) => note.transition(style, idx));
        break;
      case 'None':
        this.addTween({
          targets: this.pitchLines,
          alpha: 1,
          duration: 0,
        });
        this.noteArray.forEach((note, idx) => note.transition(style, idx));
        break;
    }
  }

  addTween(
    tween: Phaser.Tweens.Tween | Phaser.Types.Tweens.TweenBuilderConfig
  ) {
    this.tweenHandles.push(this.scene.tweens.add(tween));
  }

  checkAllNotes() {
    for (const note of this.notes) {
      if (
        note[1].timingState === undefined &&
        Object.keys(note[1]).contains('timestamp')
      ) {
        console.warn('undefined', note[1].timingState);
        return undefined;
      } else if (
        note[1].timingState !== ProgressState.Ended &&
        Object.keys(note[1]).contains('timestamp')
      ) {
        return false;
      }
    }
    return true;
  }

  destroy() {
    this.pitchLines.forEach((l) => l.destroy());
    this.pitchLines.clear();
    this.barWidths.clear();
    this.notes.forEach((note) => note.destroy());
    this.notes.clear();
    this.timeSignatureGraphics?.map((t) => t.destroy());
    this.timeSignatureGraphics?.clear();
    this.clefSymbol?.destroy();
  }

  reset(
    data: ClefData,
    measureData: MeasureProps[],
    clefSymbol?: Phaser.GameObjects.Image,
    timeSignature?: Phaser.GameObjects.Image[]
  ) {
    this._data = data;
    this.measureData = measureData;
    if (clefSymbol) {
      this.clefSymbol = clefSymbol;
      clefSymbol.setOrigin(0, 0.5);
      clefSymbol.setAlpha(0);
      const yPos =
        this._data.clef === 'bass'
          ? this.pitchLines[1].y
          : this.pitchLines[3].y + 3 * 1;
      clefSymbol.setPosition(this.config.clefSymbolMargin + this.x, yPos);
      this.add(clefSymbol);
      if (timeSignature) {
        timeSignature.forEach((item, idx) => {
          item.setOrigin(0.5, 0.5);
          item.setPosition(
            clefSymbol.x +
            clefSymbol.displayWidth +
            this.config.clefSymbolMargin * 2,
            this.pitchLines[3 - 2 * (1 - idx)].y
          );
          this.add(item);
          item.setAlpha(0);
        });
        this.timeSignatureGraphics = timeSignature;
      }
    }
    this.createNotes(measureData);
  }

  getFirstNotePosition(offset: number) {
    let minX: number | undefined = undefined;
    let minNote: VoiceGraphic | undefined = undefined;
    this.notes.forEach((note) => {
      const noteX = note.getPosition().x;
      const measure = note.measureIdx;
      if (measure < offset) return;
      if (!minX) {
        minX = noteX;
        minNote = note;
      } else {
        if (noteX < minX) {
          minX = Math.min(minX, noteX);
          minNote = note;
        }
      }
    });
    console.debug('min note', minNote, minX);
    return minX ?? Infinity;
  }

  getNotePosition(id: string) {
    const noteX = this.notes.get(id)?.getPosition().x;
    if (noteX) return noteX + this.x;
  }

  getNoteNumber(note: BaseNote) {
    switch (note) {
      case 'C':
      case 'C#':
        return 0;
      case 'D':
      case 'D#':
        return 1;
      case 'E':
        return 2;
      case 'F':
      case 'F#':
        return 3;
      case 'G':
      case 'G#':
        return 4;
      case 'A':
      case 'A#':
        return 5;
      case 'B':
        return 6;
      default:
        console.error(`invalid base note: ${note}`);
        return null;
    }
  }

  getNoteLineNumber([step, octave]: [BaseNote, number]) {
    const num = this.getNoteNumber(step);
    const oct = (this._data.octaveOffset - octave) * 7;
    const line = oct - num!;
    switch (this._data.clef) {
      case 'treble': {
        return line + 38;
      }
      case 'bass': {
        return line + 26;
      }
    }
  }

  createNotes(measureData: MeasureProps[]) {
    const { globalScaling } = this.config;
    console.debug('notes', this._data);
    this.noteArray = this._data.notes
      .sort((a, b) => a.x - b.x)
      .map(
        ({ x, voice, length, id, timestamp, measureIdx, fingering }, idx) => {
          const xPos =
            x * globalScaling +
            measureData[measureIdx].firstNoteRelativeOffset! +
            measureData[measureIdx].left!;
          if (voice === 'rest') {
            return new RestGraphic(
              this.scene,
              measureIdx,
              id,
              xPos,
              4,
              length,
              this.config,
              this,
              idx,
              this.timeSig!.numerator
            );
          } else {
            const line = this.getNoteLineNumber(voice.note);
            return new NoteGraphic(
              this.scene,
              measureIdx,
              id,
              this,
              xPos,
              line,
              length,
              this.config,
              timestamp,
              idx,
              getMidiNumberFromNoteTuple(voice.note),
              fingering
            );
          }
        }
      ).sort((a, b) => a.x - b.x);
    this.noteArray
      .forEach((val) => val && this.notes.set(val.id, val));
  }

  createLines() {
    const { pitchLineGap, phraseWidth, pitchLineWidth } = this.config;
    this.pitchLines = [0, 1, 2, 3, 4].map(
      (i) =>
        new GameObjects.Rectangle(
          this.scene,
          0,
          i * pitchLineGap,
          phraseWidth,
          pitchLineWidth,
          0x0,
          1
        )
    );
    this.pitchLines.forEach((line) => {
      this.add(line);
      line.setOrigin(0, 0);
      line.setAlpha(0);
    });
  }
}

export interface CursorData {
  measureRects: MeasureRect[];
  serializedIterator: PrerenderedPhraseData[];
}

export interface MeasureProps {
  width: number;
  left: number;
  firstNoteRelativeOffset: number;
}

interface MeasureRect {
  x: number;
  width: number;
}

export interface BaseData {
  bassClef: ClefData;
  trebleClef: ClefData;
  measureData: MeasureProps[];
  timeSignature?: TimeSignature;
  showTimeSig?: boolean;
  showBrace?: boolean;
  endMeasure?: boolean;
}

export class PhraseData implements BaseData {
  bassClef: ClefData;
  trebleClef: ClefData;
  measureData: MeasureProps[] = [];
  timeSignature: TimeSignature;
  phraseIdx: number;
  serializedIterator: PrerenderedPhraseData[];
  startTimestamp: number;
  uuid: string;
  showBrace?: boolean;
  endMeasure?: boolean;
  showTimeSig: boolean;

  constructor(
    phraseIdx: number,
    cursorData: CursorData,
    uuid: string,
    timeSignature: TimeSignature,
    showTimeSig: boolean,
    showBrace?: boolean,
    endMeasure?: boolean
  ) {
    this.uuid = uuid;
    this.startTimestamp = 0;
    this.timeSignature = timeSignature;
    this.showTimeSig = showTimeSig;
    this.phraseIdx = phraseIdx;
    this.serializedIterator = cursorData.serializedIterator;
    this.showBrace = showBrace;
    this.endMeasure = endMeasure;
    let totalWidth = 0;
    const noteAdjustmentValues: number[] = [];
    this.measureData = cursorData.measureRects.map((rect, indx) => {
      const left = totalWidth;
      const notes = cursorData.serializedIterator
        .filter(({ currentMeasureIndex }) => currentMeasureIndex === indx)
        .flatMap((slice) =>
          slice.currentVoiceEntries.flatMap(({ Notes }) =>
            Notes.flatMap((note) => note.graphics)
          )
        )
        .sort((a, b) => a.staveNote.left! - b.staveNote.left!);
      const firstNote = notes.at(0)!;
      const firstNoteRelativeOffset = firstNote.staveNote.left! - rect.x;
      const width = rect.width - firstNoteRelativeOffset;
      totalWidth += width;
      noteAdjustmentValues[indx] = firstNote.staveNote.left! - left;
      return {
        left,
        width,
        firstNoteRelativeOffset,
      };
    });
    // Since the notes in the serialized iterator are the actual x positions of the
    // elements in the source svg and therefore not adjusted to account for any gaps,
    // we remove the gaps and then transform and add them to the appropriate clef.
    const trebleNotes: VoiceData[] = [];
    const bassNotes: VoiceData[] = [];
    cursorData.serializedIterator.forEach(
      ({ currentTimestamp, currentVoiceEntries, currentMeasureIndex }) => {
        currentVoiceEntries.forEach(({ Notes }) =>
          Notes.forEach(
            ({
              fingering,
              isRest,
              graphics,
              pitch,
              staffPosition,
              length,
              printObject,
            }) => {
              if (!printObject) return;
              const x =
                graphics[0].staveNote.left! -
                (noteAdjustmentValues[currentMeasureIndex] +
                  this.measureData[currentMeasureIndex].left);
              const voice = {
                fingering,
                x,
                id: graphics[0].staveNote.id,
                timestamp: currentTimestamp,
                voice: isRest ? 'rest' : { note: noteFromOsmdPitch(pitch) },
                length,
                measureIdx: currentMeasureIndex,
                staffPosition,
              } as VoiceData;
              if (staffPosition === 'top') {
                trebleNotes.push(voice);
              } else {
                bassNotes.push(voice);
              }
            }
          )
        );
      }
    );
    this.trebleClef = {
      notes: trebleNotes,
      clef: 'treble',
      octaveOffset: 0,
    };
    this.bassClef = {
      notes: bassNotes,
      clef: 'bass',
      octaveOffset: 0,
    };
  }
}

export class PhraseGraphicBase<
  DataType extends BaseData,
> extends GameObjects.Container {
  bassClef?: Clef;
  trebleClef?: Clef;
  config: Config;
  _data: DataType;

  notes: Map<string, VoiceGraphic> = new Map();

  measureLines: GameObjects.Rectangle[] = [];
  brace?: Phaser.GameObjects.Image;
  totalWidth!: number;

  tweenHandles: Phaser.Tweens.Tween[] = [];

  constructor(
    scene: Scene,
    x: number,
    config: Partial<Config>,
    data: DataType,
    transition: StaffTransition
  ) {
    super(scene);
    this._data = data;
    this.scene = scene;
    this.config = DefaultConfig(config);
    this.notes = new Map();
    this.reset(data, x);
    this.transition(transition);
    this.scene.add.existing(this);
  }

  checkAllNotes(): boolean {
    const bass = this.bassClef?.checkAllNotes();
    const treble = this.trebleClef?.checkAllNotes();
    if (bass === undefined || treble === undefined) {
      console.warn('some notes have not finished playing yet!');
    }
    return bass === true && treble === true;
  }

  addTween(
    tween: Phaser.Tweens.Tween | Phaser.Types.Tweens.TweenBuilderConfig
  ) {
    this.tweenHandles.push(this.scene.tweens.add(tween));
  }

  destroy() {
    this.measureLines.forEach((l) => l.destroy());
    this.measureLines.clear();
    this.bassClef?.destroy();
    this.trebleClef?.destroy();
    this.notes.clear();
    this.tweenHandles.forEach(tween => tween.remove());
    this.tweenHandles.clear();
    super.destroy();
  }

  getNextPosition() {
    return this.x + this.getWidth();
  }

  getWidth() {
    return this.totalWidth;
  }

  getNoteById(id: string) {
    return this.notes.get(id);
  }

  getNotePosition(id: string) {
    const noteX =
      this.trebleClef!.getNotePosition(id) ??
      this.bassClef!.getNotePosition(id);
    if (noteX) return noteX! + this.x;
  }

  getFirstNotePosition(measure?: number) {
    return (
      Math.min(
        this.trebleClef!.getFirstNotePosition(measure ?? 0),
        this.bassClef!.getFirstNotePosition(measure ?? 0)
      ) + this.x
    );
  }

  getX() {
    return this.x;
  }

  getHeight() {
    let { pitchLineGap, staffSpacingVertical } = this.config;
    return pitchLineGap * 8 + staffSpacingVertical;
  }

  transition(style: StaffTransition) {
    switch (style) {
      case "Draw":
        const lineHeight = this.measureLines[0].height;
        const braceX = this.brace?.x;
        this.measureLines.forEach(line => line.height = 0);
        this.brace?.setX(this.brace.x - 50);
        this.measureLines.forEach((line, idx) => this.addTween({
          targets: line,
          delay: idx * 50,
          alpha: 1,
          height: lineHeight,
          ease: "Power2",
          duration: 300
        }));
        if (this.brace) this.addTween({
          targets: [this.brace],
          x: braceX,
          alpha: 1,
          delay: 500,
          duration: 300,
          ease: "Power2",
        });
        this.bassClef?.transition(style);
        this.trebleClef?.transition(style);
        break;
      case "Fade":
        this.addTween({
          targets: [...this.measureLines, ...[this.brace]],
          alpha: 1,
          duration: 1000,
          ease: "Power2"
        });
        this.bassClef?.transition(style);
        this.trebleClef?.transition(style);
        break;
      case "None":
        this.measureLines.forEach(line => line.setAlpha(1));
        this.brace?.setAlpha(1);
        this.bassClef?.transition(style);
        this.trebleClef?.transition(style);
        break;
    }
  }

  reset(data: DataType, xPos: number) {
    let { globalScaling, centreAlign, phraseWidth } = this.config;
    const height = this.getHeight();
    if (data.showBrace) {
      this.brace = this.scene.add.image(-5 * 1, height / 2, 'brace');
      const braceScale = this.brace.displayHeight / height;
      this.brace.setScale(1.02 / braceScale);
      this.brace.setOrigin(1, 0.5);
      this.brace.alpha = 0;
      this.add(this.brace);
    }
    this._data = data;
    this.notes = new Map();
    let trebleClefSymbol;
    let bassClefSymbol;
    let timeSig;
    // Compute the new widths of every measure by taking into account
    // the configured padding values and other symbols that might take
    // up any space
    let acc = 0;
    data.measureData = data.measureData.map(({ width }, idx) => {
      let gap = this.config.firstNoteGap;
      // the first measure will include the clef symbols and time
      // signature (optional)
      if (idx === 0) {
        // The clef symbols are created here and then passed onto the Clef
        // object for proper organisation. They must be created here as the
        // overall positioning and size numbers depend on their size.
        // The x, y values for the position can be zero as they're not
        // important for computing the size. The placement is handled
        // by the parent Clef object instead.
        trebleClefSymbol = this.scene.add.image(0, 0, 'treble_clef');
        // The height of the clef symbol happens to be rather proportional
        // relative to the gap between the pitch lines. After some trial
        // and error, I have found the following ratio to work quite well
        // for the proportions. This might change if the actual asset file
        // for the clef symbols are modified so tread carefully.
        const clefGapRatio = 1 / 27;
        const clefScale = clefGapRatio * this.config.pitchLineGap;
        trebleClefSymbol.setScale(clefScale);
        trebleClefSymbol.setRotation(-0.05);
        trebleClefSymbol.setAlpha(0);
        bassClefSymbol = this.scene.add.image(0, 0, 'bass_clef');
        bassClefSymbol.setScale(clefScale);
        bassClefSymbol.setAlpha(0);
        // It is important to draw both clef's since we'll be taking the
        // widest of the two to compute the necessary gap dynamically.
        const clefSymbolWidth = Math.max(
          trebleClefSymbol.displayWidth,
          bassClefSymbol.displayWidth
        );
        gap =
          clefSymbolWidth +
          this.config.firstNoteGap +
          this.config.clefSymbolMargin;
        // We have to repeat the process for the timeSignature symbols as well
        if (data.timeSignature && data.showTimeSig) {
          const sig = data.timeSignature;
          const timeSigNumText = [0, 1].map((_) =>
            this.scene.add.image(0, 0, `time_sig_${sig.numerator}`)
          );
          const timeSigDenText = [0, 1].map((_) =>
            this.scene.add.image(0, 0, `time_sig_${sig.denominator}`)
          );
          // The time signature numbers should have heights that equal twice the gap between two pitch lines
          const sc1 =
            timeSigNumText[0].displayHeight / (2 * this.config.pitchLineGap);
          const sc2 =
            timeSigDenText[0].displayHeight / (2 * this.config.pitchLineGap);
          timeSigNumText.forEach((text) => text.setScale(1 / sc1));
          timeSigDenText.forEach((text) => text.setScale(1 / sc2));
          timeSig = [
            [timeSigNumText[0], timeSigDenText[0]],
            [timeSigNumText[1], timeSigDenText[1]],
          ];
          const w = Math.max(
            timeSigNumText[0].displayWidth,
            timeSigDenText[0].displayWidth
          );
          gap += w + this.config.clefSymbolMargin;
        }
      }
      const newWidth: number = width * globalScaling + gap;
      acc += newWidth;
      return {
        left: acc - newWidth,
        width: newWidth,
        firstNoteRelativeOffset: gap,
      };
    });
    const fullWidth = data.measureData
      .map(({ width }) => width)
      .reduce((prev, val) => prev + val, 0);
    this.config.phraseWidth = fullWidth;
    this.bassClef = new Clef(
      this.scene,
      this.config,
      data.bassClef,
      data.measureData,
      data.timeSignature,
      bassClefSymbol,
      timeSig ? timeSig[0] : undefined
    );
    this.trebleClef = new Clef(
      this.scene,
      this.config,
      data.trebleClef,
      data.measureData,
      data.timeSignature,
      trebleClefSymbol,
      timeSig ? timeSig[1] : undefined
    );
    let highestNotePos = Infinity;
    let lowestNotePos = -Infinity;
    this.bassClef.notes.forEach((note) => {
      const { x, y, width, height } = note.getBounds();
      if (y + height > lowestNotePos)
        lowestNotePos = y + height;
      /*this.add(
        this.scene.add.rectangle(
          x + width / 2,
          y + height / 2,
          width,
          height,
          0,
          0.5
        )
      );*/
      this.notes.set(note.id, note);
    });
    this.trebleClef.notes.forEach((note) => {
      const { x, y, width, height } = note.getBounds();
      if (y < highestNotePos) {
        highestNotePos = y;
      }
      /*this.add(
        this.scene.add.rectangle(
          x + width / 2,
          y + height / 2,
          width,
          height,
          0,
          0.5
        )
      );*/
      console.debug('fingering ', this.trebleClef!.y);
      this.notes.set(note.id, note);
    });
    const h = this.getHeight() + 20;
    this.bassClef.notes.forEach((note) =>
      note.showFingering(
        this,
        Math.min(highestNotePos - 10, -10),
        Math.max(lowestNotePos + 10, h),
        'bottom',
        this.config!.globalScaling
      )
    );
    this.trebleClef.notes.forEach((note) =>
      note.showFingering(
        this,
        Math.min(highestNotePos - 10, -10),
        Math.max(lowestNotePos + 10, h + 10),
        'top',
        this.config!.globalScaling
      )
    );
    this.add(this.bassClef);
    this.add(this.trebleClef);
    const grandStaffHeight =
      this.config.pitchLineGap * 8 +
      this.config.pitchLineWidth * 2 +
      this.config.staffSpacingVertical;
    const strokeSize = this.config.measureLineWidth;
    let totalWidth = 0;
    this.measureLines = data.measureData.map(({ left, width }) => {
      totalWidth += width;
      return this.scene.add.rectangle(
        left,
        0,
        strokeSize,
        grandStaffHeight,
        0x0,
        1
      );
    });
    this.measureLines.push(
      this.scene.add.rectangle(
        totalWidth - (data.endMeasure ? 20 : 0),
        0,
        strokeSize,
        grandStaffHeight - this.config.pitchLineWidth,
        0x0,
        1
      )
    );
    if (data.endMeasure) {
      this.measureLines.push(
        this.scene.add.rectangle(
          totalWidth,
          0,
          strokeSize * 5,
          grandStaffHeight - this.config.pitchLineWidth,
          0x0,
          1
        )
      );
    }
    this.measureLines.forEach((line) => {
      line.setOrigin(0, 0);
      this.add(line);
      line.setAlpha(0);
    });
    this.totalWidth = totalWidth + (this.brace?.displayWidth ?? 0);
    if (centreAlign) {
      this.x = this.scene.cameras.main.width / 2 - this.totalWidth / 2;
      this.y = this.scene.cameras.main.height / 2 - this.getHeight() / 2;
    } else {
      this.x = xPos;
    }
  }
}

export class PhraseGraphic extends PhraseGraphicBase<PhraseData> { }
